/* FairyBaby hero performance patch v1.1.0.
 * Reuses the theme's Flickity import-map entries on both desktop and mobile.
 * The first slide is visible before this file executes, including without JS.
 */
if (!customElements.get('fb-hero-slideshow')) {
  let libraryPromise;
  const loadCarousel = () => {
    if (!libraryPromise) {
      libraryPromise = import('vendor.flickity')
        .then(() => import('vendor.flickity-fade'))
        .then(() => {
          if (!window.Flickity) throw new Error('Theme Flickity is unavailable');
          return window.Flickity;
        })
        .catch(error => { libraryPromise = null; throw error; });
    }
    return libraryPromise;
  };

  class FairyBabyHero extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.slider = this.querySelector('.fb-hero');
      if (!this.slider) return;
      this.slides = [...this.slider.querySelectorAll('.slideshow__slide')];
      if (!this.slides.length) return;
      this.controller = new AbortController();
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.current = 0;
      this.inView = false;
      this.editorSelected = false;
      this.timer = null;
      this.generation = (this.generation || 0) + 1;
      this.listen(this.reducedMotion, 'change', () => this.schedule());
      this.listen(document, 'visibilitychange', () => this.schedule());
      this.listen(document, 'shopify:block:select', event => {
        if (!this.contains(event.target)) return;
        const index = this.slides.findIndex(slide => slide.dataset.id === event.detail?.blockId);
        if (index < 0) return;
        this.editorSelected = true;
        this.stopTimer();
        this.materialize(index);
        if (this.carousel) this.carousel.select(index, false, true);
        else this.showStatic(index);
      });
      this.listen(document, 'shopify:block:deselect', event => {
        if (!this.contains(event.target)) return;
        this.editorSelected = false;
        this.schedule();
      });
      this.observer = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting;
        if (this.inView) this.mount();
        this.schedule();
      });
      this.observer.observe(this);
      this.showStatic(0);
    }

    listen(target, type, callback) {
      target.addEventListener(type, callback, { signal: this.controller.signal });
    }

    materialize(index) {
      const slide = this.slides[index];
      const template = slide?.querySelector('template[data-fb-slide-media]');
      if (template) {
        template.replaceWith(template.content.cloneNode(true));
        slide.querySelectorAll('img').forEach(image => { image.loading = 'eager'; });
      }
      // Hidden slide images stay inside inert template content until needed.
    }

    showStatic(index) {
      this.current = index;
      this.materialize(index);
      this.slides.forEach((slide, i) => {
        slide.classList.toggle('is-selected', i === index);
        slide.toggleAttribute('inert', i !== index);
        slide.setAttribute('aria-hidden', String(i !== index));
      });
    }

    async mount() {
      if (!this.isConnected || !this.inView || this.carousel || this.mounting || this.slides.length < 2) return;
      const generation = this.generation;
      this.mounting = true;
      try {
        const Flickity = await loadCarousel();
        if (!this.isConnected || !this.controller || generation !== this.generation) return;
        this.materialize(this.current);
        this.carousel = new Flickity(this.slider, {
          cellSelector: '.slideshow__slide',
          initialIndex: this.current,
          autoPlay: false,
          fade: true,
          setGallerySize: false,
          wrapAround: true,
          prevNextButtons: this.dataset.navigation === 'arrows',
          pageDots: ['dots', 'bars'].includes(this.dataset.navigation),
          rightToLeft: document.documentElement.dir === 'rtl',
          selectedAttraction: 0.14,
          friction: 0.8,
          dragThreshold: 7
        });
        this.carousel.on('select', () => this.selected());
        this.carousel.on('dragStart', () => this.stopTimer());
        this.carousel.on('dragEnd', () => this.schedule());
        this.setAttribute('data-fb-carousel-ready', '');
        this.selected();
      } catch (error) {
        // The server-rendered first slide remains usable if a theme asset fails.
        console.warn('[FairyBaby hero] Carousel could not start.', error);
      } finally {
        this.mounting = false;
        // A removed section may reconnect while import() is pending.
        if (generation !== this.generation && this.isConnected && this.inView) this.mount();
      }
    }

    selected() {
      this.current = this.carousel.selectedIndex;
      this.materialize(this.current);
      this.slides.forEach((slide, i) => {
        slide.toggleAttribute('inert', i !== this.current);
        slide.setAttribute('aria-hidden', String(i !== this.current));
      });
      this.schedule();
    }

    schedule() {
      this.stopTimer();
      if (!this.controller || !this.carousel || !this.inView || document.hidden ||
          this.dataset.autoplay !== 'true' || this.reducedMotion.matches || this.editorSelected) return;
      const delay = Math.max(5000, Number(this.dataset.speed) || 7000);
      const playbackVersion = this.playbackVersion;
      this.timer = setTimeout(async () => {
        this.timer = null;
        const carousel = this.carousel;
        const generation = this.generation;
        const next = (this.current + 1) % this.slides.length;
        this.materialize(next);
        // Hold the current promotion until the next image can be decoded.
        const image = this.slides[next].querySelector('img');
        if (image?.decode) await image.decode().catch(() => {});
        if (generation !== this.generation || playbackVersion !== this.playbackVersion || !this.controller || this.carousel !== carousel ||
            !this.inView || document.hidden ||
            this.editorSelected || this.reducedMotion.matches) return;
        carousel.select(next);
      }, delay);
    }

    stopTimer() {
      this.playbackVersion = (this.playbackVersion || 0) + 1;
      if (this.timer !== null) clearTimeout(this.timer);
      this.timer = null;
    }

    disconnectedCallback() {
      this.generation = (this.generation || 0) + 1;
      this.stopTimer();
      this.controller?.abort();
      this.observer?.disconnect();
      this.carousel?.destroy();
      this.carousel = null;
      this.controller = null;
    }
  }
  customElements.define('fb-hero-slideshow', FairyBabyHero);
}
